/**
 * Tela de entrada e cadastro.
 *
 * Um formulário só, alternando entre os dois modos. Duas páginas separadas
 * fariam quem errou de lado perder o que já digitou.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, MessageSquare, MonitorUp, Users, Waves } from 'lucide-react'
import { LIMITS } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Button, Field, Input } from '@/components/ui'

export function AuthView({ mode }: { mode: 'login' | 'register' }) {
  const navigate = useNavigate()
  const params = useParams<{ code?: string }>()
  const [searchParams] = useSearchParams()

  const login = useAuthStore((s) => s.login)
  const register = useAuthStore((s) => s.register)
  const status = useAuthStore((s) => s.status)

  const [identifier, setIdentifier] = useState('')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const inviteCode = params.code ?? searchParams.get('convite') ?? undefined

  useEffect(() => {
    if (status === 'authenticated') navigate('/', { replace: true })
  }, [status, navigate])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    setFieldErrors({})

    try {
      if (mode === 'login') {
        await login(identifier.trim(), password)
      } else {
        await register({
          username: username.trim().toLowerCase(),
          displayName: displayName.trim(),
          email: email.trim(),
          password,
          inviteCode,
        })
      }
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        setFieldErrors(err.fieldErrors)
      } else {
        setError('Não foi possível concluir. Tente novamente.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh">
      {/* Painel de apresentação. Some em telas estreitas para o formulário
          ocupar toda a largura disponível. */}
      <aside className="hidden flex-1 flex-col justify-between bg-surface-deepest p-12 lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-lg bg-brand">
            <MessageSquare className="size-5 text-white" />
          </div>
          <span className="text-lg font-bold text-content-primary">Nexus</span>
        </div>

        <div className="max-w-md">
          <h1 className="text-3xl leading-tight font-bold text-content-primary">
            Conversa, voz e tela no mesmo lugar
          </h1>
          <p className="mt-3 leading-relaxed text-content-secondary">
            Canais de texto e de voz, transmissão de tela em alta definição e som limpo de
            verdade. Sem instalar nada para começar.
          </p>

          <ul className="mt-8 flex flex-col gap-4">
            <Feature
              icon={<MonitorUp className="size-5" />}
              title="Compartilhamento de tela"
              description="Até 1080p, com perfis para leitura nítida ou movimento suave."
            />
            <Feature
              icon={<Waves className="size-5" />}
              title="Supressão de ruído"
              description="Teclado, ventilador e ruído de fundo ficam de fora da chamada."
            />
            <Feature
              icon={<Users className="size-5" />}
              title="Cargos e permissões"
              description="Controle por canal, do jeito que a comunidade precisar."
            />
          </ul>
        </div>

        <p className="text-xs text-content-muted">
          Feito para rodar em servidor próprio, sem custo por participante.
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center bg-surface-base p-6">
        <form onSubmit={handleSubmit} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-brand">
              <MessageSquare className="size-6 text-white" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-content-primary">
            {mode === 'login' ? 'Que bom te ver de novo' : 'Criar sua conta'}
          </h2>
          <p className="mt-1.5 text-sm text-content-secondary">
            {mode === 'login'
              ? 'Entre para continuar de onde parou.'
              : inviteCode
                ? 'Você foi convidado. Crie a conta para entrar no servidor.'
                : 'Leva menos de um minuto.'}
          </p>

          {error ? (
            <div className="mt-5 flex items-start gap-2 rounded-lg bg-danger-subtle p-3 text-sm text-danger">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-4">
            {mode === 'login' ? (
              <Field label="E-mail ou usuário" required>
                <Input
                  autoFocus
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                  placeholder="voce@exemplo.com"
                />
              </Field>
            ) : (
              <>
                <Field label="Nome de exibição" required error={fieldErrors.displayName}>
                  <Input
                    autoFocus
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={LIMITS.displayName.max}
                    autoComplete="name"
                    placeholder="Maria Silva"
                  />
                </Field>

                <Field
                  label="Nome de usuário"
                  required
                  error={fieldErrors.username}
                  hint="Letras minúsculas, números, ponto, hífen ou underline."
                >
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    maxLength={LIMITS.username.max}
                    autoComplete="username"
                    placeholder="maria"
                  />
                </Field>

                <Field label="E-mail" required error={fieldErrors.email}>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="voce@exemplo.com"
                  />
                </Field>
              </>
            )}

            <Field
              label="Senha"
              required
              error={fieldErrors.password}
              hint={mode === 'register' ? 'Mínimo de 8 caracteres.' : undefined}
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </Field>
          </div>

          <Button type="submit" size="lg" full loading={submitting} className="mt-6">
            {mode === 'login' ? 'Entrar' : 'Criar conta'}
          </Button>

          <p className="mt-5 text-center text-sm text-content-secondary">
            {mode === 'login' ? (
              <>
                Ainda não tem conta?{' '}
                <Link to="/cadastro" className="font-medium text-brand hover:underline">
                  Criar agora
                </Link>
              </>
            ) : (
              <>
                Já tem conta?{' '}
                <Link to="/entrar-conta" className="font-medium text-brand hover:underline">
                  Entrar
                </Link>
              </>
            )}
          </p>
        </form>
      </main>
    </div>
  )
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <li className="flex gap-3">
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-lg',
          'bg-brand-subtle text-brand',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-content-primary">{title}</span>
        <span className="block text-sm text-content-secondary">{description}</span>
      </span>
    </li>
  )
}
